import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  addCustomLayout,
  removeCustomLayout,
  removeParentLayoutFromLayer,
  updateElement,
  updateLastMainVideo,
  updateParentLayout,
  updateParentedLayoutsList,
} from "../../../reducers/configurator/configuratorSlice";
import { useDebounce } from "../../../helpers";
import { useEffect, useState } from "react";
import { Check, Clear } from "@mui/icons-material";
import { formatKey } from "../../../reducers/configurator/utils";
import { gamesTypes } from "../utils/utils";

const PREFIX = "LayoutsModal";

const classes = {
  root: `${PREFIX}-root`,
  layoutsModalContainer: `${PREFIX}-layoutsModalContainer`,
  layoutsModalTitle: `${PREFIX}-layoutsModalTitle`,
  layoutsModalSubtitle: `${PREFIX}-layoutsModalSubtitle`,
  layoutsModalCloseButton: `${PREFIX}-layoutsModalCloseButton`,
  layoutsModalButtonsContainer: `${PREFIX}-layoutsModalButtonsContainer`,
  layoutsModalButtons: `${PREFIX}-layoutsModalButtons`,
};

const LayoutsModalContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      height: "100vh",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      [theme.breakpoints.down("md")]: {
        margin: "0px 20px",
      },
    },
    [`& .${classes.layoutsModalContainer}`]: {
      width: "600px",
      position: "relative",
      padding: "2rem",
      backgroundColor: "#303030",
      borderRadius: "10px",
      display: "flex",
      flexDirection: "column",
      [theme.breakpoints.down("md")]: {
        padding: "10px",
      },
    },
    [`& .${classes.layoutsModalTitle}`]: {
      padding: "0px 45px",
      textAlign: "center",
      marginBottom: "20px",
      [theme.breakpoints.down("md")]: {
        padding: "1rem 1rem 0 1rem",
      },
    },
    [`& .${classes.layoutsModalSubtitle}`]: {
      textAlign: "center",
      fontStyle: "italic",
      marginBottom: "40px",
    },
    [`& .${classes.layoutsModalCloseButton}`]: {
      color: "#D3AF5F",
      position: "absolute",
      top: "0px",
      right: "0px",
    },
    [`& .${classes.layoutsModalButtonsContainer}`]: {
      display: "flex",
      justifyContent: "space-evenly",
    },
    [`& .${classes.layoutsModalButtons}`]: {
      width: "150px",
      [theme.breakpoints.down("md")]: {
        width: "120px",
      },
    },
  };
});

const LayoutsModal = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const parentLayout = useSelector((state) => state.configurator.parentLayout);
  const parentLayoutsList = useSelector(
    (state) => state.configurator.parentLayoutsList
  );
  const parentedLayoutsList = useSelector(
    (state) => state.configurator.parentedLayoutsList
  );
  const lastMainVideo = useSelector(
    (state) => state.configurator.lastMainVideo
  );
  const grid = useSelector((state) => state.configurator.grid);

  const [parentedLayout, setParentedLayout] = useState("");
  const [newLayout, setNewLayout] = useState({
    input: "",
    error: false,
    tested: false,
  });
  const debouncedLayout = useDebounce(newLayout.input, 500);

  useEffect(() => {
    if (props.modifyMode)
      setParentedLayout(
        Object.keys(parentedLayoutsList).find((opt) =>
          parentedLayoutsList[opt].includes(parentLayout)
        )
      );
  }, [props.modifyMode]);

  useEffect(() => {
    if (debouncedLayout.length > 0)
      if (
        gamesTypes.includes(debouncedLayout) ||
        parentLayoutsList.find((item) => item.label === debouncedLayout)
      )
        setNewLayout({ ...newLayout, error: true, tested: true });
      else setNewLayout({ ...newLayout, error: false, tested: true });
  }, [debouncedLayout]);

  const handleDeletionValidation = () => {
    dispatch(removeCustomLayout(parentLayout));
    props.closeModal();
  };

  const addLayoutToParent = (myKey) => {
    const tmp = { ...parentedLayoutsList };
    const myContent = tmp[parentedLayout]
      ? [...tmp[parentedLayout], myKey]
      : [myKey];
    tmp[parentedLayout] = myContent;
    dispatch(updateParentedLayoutsList(tmp));
  };

  const updateParentsLayout = (myKey) => {
    const tmp = { ...parentedLayoutsList };
    const oldKey = Object.keys(tmp).find((opt) => tmp[opt].includes(myKey));
    if (oldKey) {
      // unlink a layer to a parent
      dispatch(removeParentLayoutFromLayer(myKey));
      tmp[oldKey] = [];
    }
    if (parentedLayout) {
      // link a layer to parent
      const myContent = tmp[parentedLayout]
        ? [...tmp[parentedLayout], myKey]
        : [myKey];
      tmp[parentedLayout] = myContent;
      // if parent and child have a mainVideo attribute to true, the child one goes to false;
      const existInMainVideo = lastMainVideo.find((item) =>
        item.layout.includes(myContent)
      );
      if (existInMainVideo) {
        // update the screen
        dispatch(
          updateElement({
            screenOldLayout: existInMainVideo.data.layout,
            layer: existInMainVideo.data.rotation,
            data: { ...existInMainVideo.data, mainVideo: false },
            tile: {
              row: existInMainVideo.startTile.row,
              column: existInMainVideo.startTile.column,
            },
            tileEnd: {
              row: existInMainVideo.endTile.row,
              column: existInMainVideo.endTile.column,
            },
          })
        );
        // then update the lastMainVideo store variable
        dispatch(updateLastMainVideo(existInMainVideo));
      }
    }
    dispatch(updateParentedLayoutsList(tmp));
  };

  const handleModifyLayout = () => {
    if (!props.modifyMode) {
      const myKey = formatKey(newLayout.input);
      dispatch(
        addCustomLayout({ value: myKey, label: newLayout.input, key: myKey })
      );
      if (parentedLayout.length > 0) addLayoutToParent(myKey);
      dispatch(updateParentLayout(myKey));
    } else {
      updateParentsLayout(parentLayout);
    }

    props.closeModal();
  };

  const areBothGridSameSize = (key) => {
    return (
      grid[key].length === grid[parentLayout].length &&
      grid[key][0].length === grid[parentLayout][0].length
    );
  };

  return (
    <LayoutsModalContainer>
      <Box className={classes.root}>
        <Box className={classes.layoutsModalContainer}>
          <IconButton
            className={classes.layoutsModalCloseButton}
            aria-label="close"
            onClick={props.closeModal}
            size="large"
          >
            <CloseIcon />
          </IconButton>
          {props.deleteMode ? (
            <Box>
              <Typography
                className={classes.layoutsModalTitle}
                variant="h4"
                component="h4"
              >
                {t("configurator.layoutsModal.validation")}
              </Typography>
              <Typography
                className={classes.layoutsModalSubtitle}
                variant="subtitle2"
                component="p"
              >
                {t("configurator.layoutsModal.validationSubtitle")}
              </Typography>
              <Box className={classes.layoutsModalButtonsContainer}>
                <Button
                  className={classes.layoutsModalButtons}
                  variant="contained"
                  color="primary"
                  onClick={handleDeletionValidation}
                >
                  {t("configurator.layoutsModal.deleteButton")}
                </Button>
                <Button
                  className={classes.layoutsModalButtons}
                  variant="contained"
                  color="primary"
                  onClick={props.closeModal}
                >
                  {t("configurator.layoutsModal.backButton")}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                className={classes.layoutsModalTitle}
                variant="h4"
                component="h4"
              >
                {props.modifyMode
                  ? t("configurator.layoutsModal.modifyLayerTitle")
                  : t("configurator.layoutsModal.addLayerTitle")}
              </Typography>
              <TextField
                label={t(
                  "configurator.layoutsModal.futurLayoutNamePlaceholder"
                )}
                value={props.modifyMode ? parentLayout : newLayout.input}
                variant="outlined"
                color="primary"
                error={newLayout.error}
                disabled={props.modifyMode}
                onChange={(e) =>
                  setNewLayout({
                    ...newLayout,
                    input: e.target.value,
                    tested: false,
                  })
                }
                InputProps={{
                  endAdornment: (
                    <>
                      {newLayout.input.length > 0 &&
                      !newLayout.error &&
                      newLayout.tested ? (
                        <Check sx={{ color: "green" }} />
                      ) : newLayout.error && newLayout.tested ? (
                        <Clear sx={{ color: "red" }} />
                      ) : null}
                    </>
                  ),
                }}
              />
              {newLayout.error && (
                <FormHelperText id="nameUsed" error>
                  {t("myExpariences.formErrors.nameUsed")}
                </FormHelperText>
              )}
              <FormControl fullWidth sx={{ marginTop: "1rem" }}>
                <InputLabel id="select-parent-layer">
                  {t("configurator.layoutsModal.linkNewLayer")}
                </InputLabel>
                <Select
                  labelId="select-parent-layer"
                  id="select-parent-layer"
                  onChange={(e) => setParentedLayout(e.target.value)}
                  value={parentedLayout || ""}
                  label={t("configurator.layoutsModal.linkNewLayer")}
                >
                  <MenuItem value="">
                    {t("configurator.layoutsModal.noneOption")}
                  </MenuItem>
                  {parentLayoutsList.map((item) => (
                    <MenuItem
                      value={item.value}
                      key={item.key}
                      disabled={
                        !areBothGridSameSize(item.key) ||
                        gamesTypes.includes(item.key) ||
                        (!props.modifyMode &&
                          parentedLayoutsList[item.value]?.length > 0) ||
                        Object.keys(parentedLayoutsList).find((opt) =>
                          parentedLayoutsList[opt].includes(item.value)
                        ) ||
                        parentedLayoutsList[item.value]?.length > 0
                          ? true
                          : false
                      }
                      sx={{
                        display:
                          props.modifyMode && parentLayout === item.value
                            ? "none"
                            : "block",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        {item.label}{" "}
                        {!areBothGridSameSize(item.key) &&
                        !gamesTypes.includes(item.key) ? (
                          <Typography
                            variant="body1"
                            sx={{ opacity: "1", color: "#f44336" }}
                          >
                            {t("configurator.layoutsModal.notSameSizeGrid")}
                          </Typography>
                        ) : (
                          ""
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                className={classes.layoutsModalButtons}
                variant="contained"
                color="primary"
                onClick={handleModifyLayout}
                sx={{ margin: "0 auto", marginTop: "1rem" }}
                disabled={
                  !props.modifyMode && (newLayout.error || !newLayout.tested)
                }
              >
                {t("configurator.layoutsModal.confirm")}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </LayoutsModalContainer>
  );
};

export default LayoutsModal;
